import { useEffect, useRef, useState } from 'react';
import type {
  InventoryFilters,
  StockStatusCounts,
} from '../../../utils/inventoryFilters';

export type DraftCounts = { counts: StockStatusCounts | null; total: number };

export const FILTER_COUNTS_DEBOUNCE_MS = 300;

/**
 * Per-status counts and the total for the Filters sheet's draft. The list
 * itself never asks the server for counts, so the sheet fetches them when it
 * opens (right away) and again after each change (debounced). Null while a
 * request is in flight, after it fails, and while the sheet is closed, so the
 * sheet shows "—" rather than numbers that don't match the draft.
 */
export const useFilterSheetCounts = (
  open: boolean,
  draft: InventoryFilters,
  fetchCounts: (draft: InventoryFilters) => Promise<DraftCounts | null>,
  debounceMs = FILTER_COUNTS_DEBOUNCE_MS,
): DraftCounts | null => {
  const [result, setResult] = useState<DraftCounts | null>(null);
  const requestRef = useRef(0);
  const openedRef = useRef(false);
  const draftKey = JSON.stringify(draft);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    if (!open) {
      // Drop any answer still on its way and start fresh next time
      requestRef.current += 1;
      openedRef.current = false;
      setResult(null);
      return;
    }
    const requestId = ++requestRef.current;
    const delay = openedRef.current ? debounceMs : 0;
    openedRef.current = true;
    setResult(null);
    const timer = setTimeout(async () => {
      let next: DraftCounts | null = null;
      try {
        next = await fetchCounts(draftRef.current);
      } catch {
        next = null;
      }
      if (requestId === requestRef.current) setResult(next);
    }, delay);
    return () => clearTimeout(timer);
    // draftKey stands in for draft so an equal draft doesn't refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftKey, fetchCounts, debounceMs]);

  return result;
};
